import { Flex, Text, useBoolean } from "@chakra-ui/react";
import Gallery from "components/containers/inspectionDetail/content/inspectionDocument/containers/gallery";
import { ItemData } from "components/containers/inspectionDetail/content/inspectionDocument/containers/gallery/Item";
import IconRevertData from "components/icon/IconRevertData";
import { FileHistory } from "interfaces/models/pin";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DATE_TIME_SLASH_FORMAT, formatDate } from "utils/date";
import { ImageWithBlackboardProps } from "../../fields/imageHasBlackboard/previewer/ImageWithBlackboard";
import { getContentImageList } from "apiClient/v2/inspection/pinContentApi";
import { logDev } from "utils/logs";

interface SelectFromGalleryProps {
  isOpen: boolean;
  value?: string;
  pinId: string;
  itemResultId: string;
  showBlackboard?: boolean;
  imageWithBlackboardProps?: ImageWithBlackboardProps;
  onChange(history: FileHistory): void;
  onDeleteImage: (
    item: ItemData,
    histories: FileHistory[]
  ) => Promise<boolean | undefined>;
  onClose(): void;
}

const SelectFromGallery: React.FC<SelectFromGalleryProps> = ({
  isOpen,
  value,
  showBlackboard,
  imageWithBlackboardProps,
  onDeleteImage,
  onChange,
  onClose,
  pinId,
  itemResultId,
}) => {
  const [histories, setHistories] = useState<FileHistory[]>([]);
  const [isLoading, loading] = useBoolean(true);

  const { items, mapItem } = useMemo(() => {
    const mapItem = new Map();
    const items = histories
      ?.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      ?.map((item) => {
        mapItem.set(item.src, item);
        const result = {
          id: item.src,
          uuid: item.id,
          src: item.src,
          title: formatDate(
            item?.lastModifiedDateFile ?? item.createdAt,
            DATE_TIME_SLASH_FORMAT
          ),
          imageWithBlackboardProps: showBlackboard
            ? {
                ...imageWithBlackboardProps,
                src: item.src,
                originSrc: item.originSrc,
                lastModifiedDateFile: item.lastModifiedDateFile,
                uploadTime: item.createdAt,
                blackboardData:
                  "blackboardData" in item ? item?.blackboardData : undefined,
              }
            : undefined,
        } as ItemData;

        return result;
      });

    return { items, mapItem };
  }, [imageWithBlackboardProps, showBlackboard, histories]);

  const getHistories = useCallback(async () => {
    loading.on();
    try {
      const response = await getContentImageList({ pinId, itemResultId });
      setHistories(response?.data);
    } catch (error) {
      logDev(error);
      /** Ignore error */
    }
    loading.off();
  }, [pinId, itemResultId, loading]);

  const afterDeleteImage = useCallback(
    async (item: ItemData) => {
      const newHistories = histories.filter((h) => h.id !== item.uuid);
      setHistories(newHistories);

      return onDeleteImage(item, newHistories);
    },
    [histories, onDeleteImage]
  );

  useEffect(() => {
    getHistories();
  }, [getHistories]);

  return (
    <Gallery
      isOpen={isOpen}
      selected={value}
      isLoading={isLoading}
      items={items}
      title={
        <Flex fill="white" flexDir="row" alignItems="center">
          <IconRevertData w="2.4rem" h="2.4rem" mr="0.5rem" />
          <Text fontWeight="700" fontSize="1.8rem">
            撮影履歴
          </Text>
        </Flex>
      }
      onSelect={(src: any) => {
        const item = mapItem.get(src);
        if (!item) return;
        onChange(item);
      }}
      onDeleteImage={afterDeleteImage}
      onBack={onClose}
    />
  );
};

export default SelectFromGallery;
